import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { PermitList } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { useListState } from '../../lib/useList';
import { Button, EmptyRow, FilterBar, LinkButton, PageHeader, Pager, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { ErrorPanel } from '../../components/ErrorPanel';
import { permitsKey, usePermitSites, usePermitTypes } from './api';
import { PermitStatusBadge } from './parts';
import { RaisePermitDrawer } from './RaisePermitDrawer';

const PAGE_SIZE = 25;

/** Permits to work: open and closed, by site and type (permits.md s5). */
export function PermitsPage() {
  const list = useListState();
  const canRaise = useCan('permits.raise');
  const canTypes = useCan('permits.manage_types');
  const [status, setStatus] = useState<'open' | 'closed' | 'all'>('open');
  const [siteId, setSiteId] = useState('');
  const [typeId, setTypeId] = useState('');
  const [raising, setRaising] = useState(false);
  const sites = usePermitSites();
  const types = usePermitTypes();
  const q = useQuery({
    queryKey: [...permitsKey, 'list', list.page, list.search, status, siteId, typeId],
    queryFn: () => api.get('/permits', { query: { page: list.page, pageSize: PAGE_SIZE, search: list.search, status, siteId, typeId }, schema: PermitList }),
  });
  const siteName = (id: string) => sites.data?.find((s) => s.id === id)?.name ?? '';
  return (
    <div className="space-y-4">
      <PageHeader
        title={t('p3pt.list.title')}
        description={t('p3pt.list.description')}
        actions={
          <>
            {canTypes ? <LinkButton to="/permits/types">{t('p3pt.list.types')}</LinkButton> : null}
            {canRaise ? (
              <Button variant="primary" onClick={() => setRaising(true)}>
                {t('p3pt.list.raise')}
              </Button>
            ) : null}
          </>
        }
      />
      <FilterBar columns="sm:grid-cols-4">
        <TextField label={t('common.search')} type="search" value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
        <SelectField
          label={t('p3pt.filter.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as typeof status);
            list.resetPage();
          }}
        >
          <option value="open">{t('p3pt.filter.open')}</option>
          <option value="closed">{t('p3pt.filter.closed')}</option>
          <option value="all">{t('p3pt.filter.all')}</option>
        </SelectField>
        <SelectField
          label={t('p3pt.field.site')}
          value={siteId}
          onChange={(e) => {
            setSiteId(e.target.value);
            list.resetPage();
          }}
        >
          <option value="">{t('p3pt.filter.anySite')}</option>
          {(sites.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('p3pt.field.type')}
          value={typeId}
          onChange={(e) => {
            setTypeId(e.target.value);
            list.resetPage();
          }}
        >
          <option value="">{t('p3pt.filter.anyType')}</option>
          {(types.data ?? []).map((x) => (
            <option key={x.id} value={x.id}>
              {x.name}
            </option>
          ))}
        </SelectField>
      </FilterBar>
      {q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Spinner />
      ) : (
        <>
          <TableWrap caption={t('p3pt.list.title')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('p3pt.col.number')}</Th>
                <Th>{t('p3pt.field.type')}</Th>
                <Th>{t('p3pt.field.holder')}</Th>
                <Th>{t('p3pt.field.site')}</Th>
                <Th>{t('p3pt.field.area')}</Th>
                <Th>{t('p3pt.col.ends')}</Th>
                <Th>{t('p3pt.filter.status')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {q.data.items.length === 0 ? (
                <EmptyRow cols={7} />
              ) : (
                q.data.items.map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-2.5 font-medium">
                      <Link className="text-[var(--brand-primary)] underline" to={`/permits/${p.id}`}>
                        {p.number}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5">{p.typeName}</td>
                    <td className="px-4 py-2.5">
                      {p.holderName}
                      {p.holderCompany ? <span className="block text-xs text-slate-600">{p.holderCompany}</span> : null}
                    </td>
                    <td className="px-4 py-2.5">{siteName(p.siteId)}</td>
                    <td className="px-4 py-2.5">{p.area}</td>
                    <td className="px-4 py-2.5">{formatDateTime(p.endsAt)}</td>
                    <td className="px-4 py-2.5">
                      <PermitStatusBadge status={p.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </TableWrap>
          <Pager page={q.data.page} pageSize={q.data.pageSize} total={q.data.total} onPage={list.setPage} />
        </>
      )}
      {raising ? <RaisePermitDrawer onClose={() => setRaising(false)} /> : null}
    </div>
  );
}
